/**
 * Response Unit Utilities
 * Validation, generation, and quality scoring for Response Units
 */

import { PersonaTones, DifficultyLevels, SalesStages } from './responseUnitFramework';

/**
 * Validate a Response Unit for completeness and quality
 * @param {Object} unit - Response Unit to validate
 * @returns {Object} Validation result with score and issues
 */
export const validateResponseUnit = (unit) => {
  const issues = [];
  let score = 100;

  // Required fields
  if (!unit.id) {
    issues.push({ severity: 'error', field: 'id', message: 'Missing unique ID' });
    score -= 20;
  }
  
  if (!unit.type || !['script', 'objection', 'hint', 'micro_lesson', 'roleplay_response'].includes(unit.type)) {
    issues.push({ severity: 'error', field: 'type', message: 'Invalid or missing type' });
    score -= 20;
  }

  if (!unit.title || unit.title.length < 10) {
    issues.push({ severity: 'warning', field: 'title', message: 'Title too short or missing' });
    score -= 5;
  }

  // Phase validation
  if (!unit.phases) {
    issues.push({ severity: 'error', field: 'phases', message: 'Missing phases object' });
    score -= 30;
  } else {
    // Acknowledge phase
    if (!unit.phases.acknowledge?.statement) {
      issues.push({ severity: 'error', field: 'acknowledge.statement', message: 'Missing acknowledgment statement' });
      score -= 10;
    } else {
      const wordCount = unit.phases.acknowledge.statement.split(' ').length;
      if (wordCount < 10 || wordCount > 40) {
        issues.push({ severity: 'warning', field: 'acknowledge.statement', message: `Word count ${wordCount} (ideal: 15-30)` });
        score -= 3;
      }
    }

    if (!unit.phases.acknowledge?.variations || unit.phases.acknowledge.variations.length < 2) {
      issues.push({ severity: 'info', field: 'acknowledge.variations', message: 'Should have 2-3 variations' });
      score -= 2;
    }

    // Probe phase
    if (!unit.phases.probe?.questions || unit.phases.probe.questions.length < 1) {
      issues.push({ severity: 'warning', field: 'probe.questions', message: 'Should have 2-3 discovery questions' });
      score -= 5;
    }

    if (!unit.phases.probe?.intent) {
      issues.push({ severity: 'info', field: 'probe.intent', message: 'Missing probe intent description' });
      score -= 2;
    }

    // Value phase
    if (!unit.phases.value?.statement) {
      issues.push({ severity: 'error', field: 'value.statement', message: 'Missing value statement' });
      score -= 15;
    } else {
      const wordCount = unit.phases.value.statement.split(' ').length;
      if (wordCount < 20 || wordCount > 80) {
        issues.push({ severity: 'warning', field: 'value.statement', message: `Word count ${wordCount} (ideal: 30-60)` });
        score -= 3;
      }
    }

    if (!unit.phases.value?.framework) {
      issues.push({ severity: 'info', field: 'value.framework', message: 'Should specify framework used' });
      score -= 2;
    }

    if (!unit.phases.value?.keyPoints || unit.phases.value.keyPoints.length < 2) {
      issues.push({ severity: 'info', field: 'value.keyPoints', message: 'Should have 2-3 key takeaways' });
      score -= 2;
    }

    // Next Step phase
    if (!unit.phases.nextStep?.cta) {
      issues.push({ severity: 'error', field: 'nextStep.cta', message: 'Missing call-to-action' });
      score -= 10;
    } else {
      const wordCount = unit.phases.nextStep.cta.split(' ').length;
      if (wordCount < 5 || wordCount > 25) {
        issues.push({ severity: 'warning', field: 'nextStep.cta', message: `CTA word count ${wordCount} (ideal: 10-20)` });
        score -= 3;
      }
    }

    if (!unit.phases.nextStep?.ctaType) {
      issues.push({ severity: 'info', field: 'nextStep.ctaType', message: 'Should specify CTA type' });
      score -= 2;
    }

    if (!unit.phases.nextStep?.alternatives || unit.phases.nextStep.alternatives.length < 1) {
      issues.push({ severity: 'info', field: 'nextStep.alternatives', message: 'Should have 1-2 alternative next steps' });
      score -= 2;
    }
  }

  // Metadata validation
  if (!unit.metadata) {
    issues.push({ severity: 'warning', field: 'metadata', message: 'Missing metadata' });
    score -= 10;
  } else {
    if (!unit.metadata.persona) {
      issues.push({ severity: 'info', field: 'metadata.persona', message: 'Should specify persona/tone' });
      score -= 2;
    }

    if (!unit.metadata.difficulty) {
      issues.push({ severity: 'info', field: 'metadata.difficulty', message: 'Should specify difficulty level' });
      score -= 2;
    }

    if (!unit.metadata.salesStage) {
      issues.push({ severity: 'info', field: 'metadata.salesStage', message: 'Should specify sales stage' });
      score -= 2;
    }
  }

  // Tags validation
  if (!unit.tags || unit.tags.length < 3) {
    issues.push({ severity: 'info', field: 'tags', message: 'Should have at least 3 tags for searchability' });
    score -= 2;
  }

  return {
    isValid: score >= 70,
    score: Math.max(0, Math.min(100, score)),
    issues,
    summary: issues.length === 0 ? 'Perfect Response Unit' : `${issues.length} issue(s) found`
  };
};

/**
 * Generate Response Unit template from AI
 * @param {Object} params - Generation parameters
 * @returns {Promise<Object>} Generated Response Unit
 */
export const generateResponseUnit = async (params) => {
  const {
    type = 'objection',
    scenario,
    persona = 'CONSULTATIVE',
    difficulty = 'intermediate',
    industry = 'general',
    salesStage = 'negotiation'
  } = params;

  // This would typically call an AI service (Puter, OpenAI, etc.)
  // For now, return template structure
  
  const template = {
    id: `${type}_generated_${Date.now()}`,
    type,
    title: scenario || `Generated ${type}`,
    
    phases: {
      acknowledge: {
        statement: '',
        tone: 'empathetic',
        variations: [],
        rationale: ''
      },
      probe: {
        questions: [],
        intent: '',
        listenFor: [],
        optional: false
      },
      value: {
        statement: '',
        framework: 'case-study',
        keyPoints: [],
        socialProof: ''
      },
      nextStep: {
        cta: '',
        ctaType: 'meeting',
        alternatives: [],
        transitionPhrase: ''
      }
    },
    
    metadata: {
      persona,
      difficulty,
      industry,
      buyerPersonas: [],
      salesStage,
      estimatedDuration: 60,
      prerequisites: [],
      successMetrics: {
        primaryGoal: '',
        fallbackGoal: '',
        avoidance: ''
      }
    },
    
    tags: [],
    qualityScore: 0
  };

  return template;
};

/**
 * Format Response Unit for display in UI
 * @param {Object} unit - Response Unit
 * @param {string} format - Display format: 'full' | 'compact' | 'practice'
 * @returns {string} Formatted text
 */
export const formatResponseUnit = (unit, format = 'full') => {
  if (!unit || !unit.phases) return '';

  switch (format) {
    case 'full':
      return `
## ${unit.title}

**Acknowledge**
${unit.phases.acknowledge.statement}

**Probe**
${unit.phases.probe.questions.map((q, i) => `${i + 1}. ${q}`).join('\n')}

**Value**
${unit.phases.value.statement}

${unit.phases.value.keyPoints ? '**Key Points:**\n' + unit.phases.value.keyPoints.map(p => `• ${p}`).join('\n') : ''}

**Next Step**
${unit.phases.nextStep.cta}
      `.trim();

    case 'compact':
      return `
**${unit.title}**

1️⃣ ${unit.phases.acknowledge.statement}
2️⃣ ${unit.phases.probe.questions[0] || 'Ask discovery question'}
3️⃣ ${unit.phases.value.keyPoints[0] || unit.phases.value.statement.substring(0, 100)}
4️⃣ ${unit.phases.nextStep.cta}
      `.trim();

    case 'practice':
      // Format for drill practice (show one phase at a time)
      return `
**Step 1: Acknowledge**
${unit.phases.acknowledge.statement}

*Now try it yourself...*
      `.trim();

    default:
      return unit.title;
  }
};

/**
 * Convert legacy content to Response Unit format
 * @param {Object} legacyContent - Old format content
 * @param {string} type - Content type
 * @returns {Object} Converted Response Unit
 */
export const convertLegacyToResponseUnit = (legacyContent, type = 'objection') => {
  // Handle old objection format
  if (type === 'objection' && legacyContent.objection_text) {
    return {
      id: legacyContent.id || `obj_legacy_${Date.now()}`,
      type: 'objection',
      title: legacyContent.objection_text,
      
      phases: {
        acknowledge: {
          statement: legacyContent.response?.split('\n')[0] || legacyContent.sample_response?.split('\n')[0] || '',
          tone: 'empathetic',
          variations: [],
          rationale: 'Converted from legacy format'
        },
        probe: {
          questions: legacyContent.discovery_questions || [],
          intent: 'Understand objection root cause',
          listenFor: [],
          optional: false
        },
        value: {
          statement: legacyContent.response || legacyContent.sample_response || '',
          framework: 'feature-benefit',
          keyPoints: legacyContent.key_points || [],
          socialProof: ''
        },
        nextStep: {
          cta: legacyContent.next_step || 'Would it make sense to discuss this further?',
          ctaType: 'meeting',
          alternatives: [],
          transitionPhrase: 'Here\'s what I\'d suggest—'
        }
      },
      
      metadata: {
        persona: 'CONSULTATIVE',
        difficulty: legacyContent.difficulty_level || 'intermediate',
        industry: legacyContent.industry || 'general',
        buyerPersonas: [],
        salesStage: 'negotiation',
        estimatedDuration: 60,
        prerequisites: [],
        successMetrics: {
          primaryGoal: 'Address objection and move forward',
          fallbackGoal: 'Build understanding',
          avoidance: 'Don\'t get defensive'
        }
      },
      
      tags: legacyContent.tags || ['objection-handling'],
      qualityScore: 70 // Legacy content needs review
    };
  }

  // Handle old script format
  if (type === 'script' && legacyContent.script_text) {
    return {
      id: legacyContent.id || `script_legacy_${Date.now()}`,
      type: 'script',
      title: legacyContent.title || legacyContent.script_type || 'Legacy Script',
      
      phases: {
        acknowledge: {
          statement: legacyContent.opening || legacyContent.script_text.split('\n')[0] || '',
          tone: 'direct',
          variations: [],
          rationale: 'Converted from legacy format'
        },
        probe: {
          questions: legacyContent.qualifying_questions || [],
          intent: 'Qualify prospect',
          listenFor: [],
          optional: false
        },
        value: {
          statement: legacyContent.value_prop || legacyContent.script_text || '',
          framework: 'case-study',
          keyPoints: [],
          socialProof: ''
        },
        nextStep: {
          cta: legacyContent.call_to_action || 'Does it make sense to schedule a follow-up?',
          ctaType: 'meeting',
          alternatives: [],
          transitionPhrase: 'Here\'s what I\'m thinking—'
        }
      },
      
      metadata: {
        persona: 'CONSULTATIVE',
        difficulty: legacyContent.difficulty_level || 'beginner',
        industry: legacyContent.industry || 'general',
        buyerPersonas: [],
        salesStage: legacyContent.script_type === 'cold_call' ? 'prospecting' : 'discovery',
        estimatedDuration: 45,
        prerequisites: [],
        successMetrics: {
          primaryGoal: 'Book meeting',
          fallbackGoal: 'Send follow-up materials',
          avoidance: 'Don\'t pitch too early'
        }
      },
      
      tags: legacyContent.tags || ['script'],
      qualityScore: 70
    };
  }

  return null;
};

/**
 * Search Response Units by criteria
 * @param {Array} units - Array of Response Units
 * @param {Object} filters - Search filters
 * @returns {Array} Filtered units
 */
export const searchResponseUnits = (units, filters = {}) => {
  const {
    type,
    persona,
    difficulty,
    industry,
    salesStage,
    tags,
    minQualityScore = 0,
    query
  } = filters;

  return units.filter(unit => {
    // Type filter
    if (type && unit.type !== type) return false;

    // Persona filter
    if (persona && unit.metadata?.persona !== persona) return false;

    // Difficulty filter
    if (difficulty && unit.metadata?.difficulty !== difficulty) return false;

    // Industry filter
    if (industry && industry !== 'general') {
      const unitIndustry = unit.metadata?.industry || 'general';
      if (unitIndustry !== 'general' && unitIndustry !== industry) return false;
    }

    // Sales stage filter
    if (salesStage && unit.metadata?.salesStage !== salesStage && unit.metadata?.salesStage !== 'all') {
      return false;
    }

    // Tags filter
    if (tags && tags.length > 0) {
      const hasTag = tags.some(tag => unit.tags?.includes(tag));
      if (!hasTag) return false;
    }

    // Quality score filter
    if (unit.qualityScore < minQualityScore) return false;

    // Text search
    if (query) {
      const searchText = `${unit.title} ${unit.phases.acknowledge.statement} ${unit.phases.value.statement}`.toLowerCase();
      if (!searchText.includes(query.toLowerCase())) return false;
    }

    return true;
  });
};

export default {
  validateResponseUnit,
  generateResponseUnit,
  formatResponseUnit,
  convertLegacyToResponseUnit,
  searchResponseUnits
};
